/**
 * Krok UXV4b — LIDSKY TEXT nad kody konceptu inzeratu.
 *
 * Server posila puvod (`cadastre`…), kod kontroly a pole portalu; vety
 * sklada tenhle modul z katalogu. Popisky poli a hodnot (`listingDraft.field.*`,
 * `listingDraft.value.*`) jsou TYTEZ, ze kterych server sklada text inzeratu
 * a podklad pro AI — jedno misto, ne kopie v adminu.
 */
import { t, tryT, type MessageKey } from '@reos/i18n';
import { moneyInputAmount, parseMoneyInput } from './money-text.js';
import type {
  DraftCheck,
  DraftFieldView,
  DraftOrigin,
  DraftPortalId,
  PortalIssue,
  PublishDecision,
} from './listing-draft-api.js';

const cs = { locale: 'cs' } as const;

function tk(key: string, values?: Record<string, unknown>): string {
  return t(key as MessageKey, values === undefined ? cs : { ...cs, values });
}

const number = new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 2 });
const compact = new Intl.NumberFormat('cs-CZ', { notation: 'compact', maximumFractionDigits: 1 });

/**
 * Krok UXP1 (nalez P1-3): popisek pole SKLADA KLIC Z DAT SERVERU, takze
 * neznamy kod pole je provozni stav, ne programatorska chyba. `t()` na
 * chybejicim klici zamerne vyhazuje (hlida parita katalogu), a tohle volani ho
 * tim shodilo cely strom administrace na anglicke „Something went wrong!".
 * Nahradni text je obecny, ale cesky — obrazovka zustane pouzitelna.
 */
export function fieldLabel(key: string): string {
  return tryT(`listingDraft.field.${key}` as MessageKey, cs) ?? tk('listingDraft.field.other');
}

export function originLabel(origin: DraftOrigin): string {
  return tk(`webAdmin.listingDraft.origin.${origin}`);
}

export function portalName(portal: DraftPortalId): string {
  return tk(`webAdmin.listingDraft.portal.${portal}`);
}

/** „z katastru, registru budov a průkazu energetické náročnosti". */
export function originsSentence(origins: readonly DraftOrigin[]): string | null {
  const parts = origins.map((origin) => tk(`webAdmin.listingDraft.originFrom.${origin}`));
  if (parts.length === 0) return null;
  const list =
    parts.length === 1
      ? (parts[0] ?? '')
      : `${parts.slice(0, -1).join(', ')} ${tk('webAdmin.listingDraft.and')} ${parts[parts.length - 1] ?? ''}`;
  return tk('webAdmin.listingDraft.data.fromSources', { sources: list });
}

export function kindLabel(propertyKind: string): string {
  // Krok APP2 bod 4: pozemek uz neni „nemovitost" — ma vlastni slovo, protoze
  // se u nej liší cely formular (vymera a druh pozemku misto udaju stavby).
  const kind =
    propertyKind === 'unit' || propertyKind === 'building' || propertyKind === 'land'
      ? propertyKind
      : 'other';
  return tk(`webAdmin.listingDraft.data.kind.${kind}`);
}

function money(value: unknown): string | null {
  if (typeof value !== 'object' || value === null) return null;
  const amount = Number((value as { amount?: unknown }).amount);
  if (!Number.isFinite(amount)) return null;
  return tk('webAdmin.listingDraft.value.czk', { amount: number.format(amount) });
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Krok P8F8 bod 1 — DOSTUPNOST JAKO `now`, DATUM NEBO TEXT MAJITELE.
 *
 * Fakt `sale.available_from` je text (registr `FACT_KEYS`), takze „Po dohodě"
 * je platna hodnota — ne chyba vstupu (nalez P8-N14). Tahle funkce je JEDINE
 * misto, kde se rika, jak se dostupnost cte: pouziva ji radek tabulky
 * (`formatValue`) i pole editoru (`editorText`), aby netvrdily dve veci.
 */
function availabilityText(value: unknown): string | null {
  if (value === 'now') return tk('webAdmin.listingDraft.value.now');
  if (typeof value !== 'string') return null;
  if (!ISO_DATE.test(value)) return value;
  return new Date(`${value}T12:00:00Z`).toLocaleDateString('cs-CZ');
}

/** Hodnota pole tak, jak ji ukazuje radek („87 m²", „ano", „osobní"). */
export function formatValue(field: Pick<DraftFieldView, 'valueType' | 'unit' | 'value'>): string | null {
  const { value } = field;
  if (value === null || value === undefined) return null;
  switch (field.valueType) {
    case 'number':
      return typeof value === 'number'
        ? `${number.format(value)}${field.unit === 'm2' ? ' m²' : ''}`
        : null;
    case 'integer':
      return typeof value === 'number' ? String(value) : null;
    case 'boolean':
      return tk(value === true ? 'listingDraft.value.yes' : 'listingDraft.value.no');
    // Vycty: hodnota je kod, cesky text je v katalogu pod jmenem typu.
    // Krok APP2 bod 4: `land_kind` (druh pozemku) jde tutez cestou jako druh domu.
    case 'ownership':
    case 'structure':
    case 'condition':
    case 'house_kind':
    case 'land_kind':
      return tk(`listingDraft.value.${field.valueType}.${String(value)}`);
    case 'hoa': {
      /*
       * Krok PROP5 — MESICNI PRISPEVEK A STAV FONDU NEJSOU TOTEZ. Do PROP5
       * tu bylo jedno pole a obe castky se vypisovaly jako „fond oprav",
       * takze 4 700 000 Kc stalo na miste mesicniho prispevku (nalez 19).
       */
      const hoa = value as {
        name: string | null;
        contributionMonthlyCzk: number | null;
        repairFundBalanceCzk: number | null;
      };
      if (hoa.contributionMonthlyCzk !== null) {
        return tk('webAdmin.listingDraft.value.hoaContribution', {
          amount: compact.format(hoa.contributionMonthlyCzk),
        });
      }
      if (hoa.repairFundBalanceCzk !== null) {
        return tk('webAdmin.listingDraft.value.hoaFund', {
          amount: compact.format(hoa.repairFundBalanceCzk),
        });
      }
      return hoa.name;
    }
    case 'money':
      return money(value);
    case 'date_or_now':
      return availabilityText(value);
    default:
      return String(value);
  }
}

/** Patro jako v predloze: „4., bez výtahu" (vytah se pripoji, kdyz ho zname). */
export function formatFloor(floor: unknown, elevator: unknown): string | null {
  if (typeof floor !== 'number') return null;
  const base = floor === 0 ? tk('webAdmin.listingDraft.value.groundFloor') : tk('webAdmin.listingDraft.value.floor', { floor });
  if (elevator === true) return `${base}, ${tk('webAdmin.listingDraft.value.withElevator')}`;
  if (elevator === false) return `${base}, ${tk('webAdmin.listingDraft.value.noElevator')}`;
  return base;
}

/** Radek „Kontrola". `price_missing` tu neni — cenu ukazuje „Doplň". */
export function checkText(check: DraftCheck, propertyKind: string): string | null {
  const kind = propertyKind === 'unit' || propertyKind === 'building' ? propertyKind : 'other';
  switch (check.code) {
    case 'matches_cadastre':
      return tk('webAdmin.listingDraft.check.matches_cadastre');
    case 'area_mismatch':
      return tk('webAdmin.listingDraft.check.area_mismatch', {
        official: number.format(check.official),
        claimed: number.format(check.claimed),
        origin: originLabel(check.claimedOrigin),
      });
    case 'disposition_mismatch':
      return tk('webAdmin.listingDraft.check.disposition_mismatch', {
        official: check.official,
        claimed: check.claimed,
        origin: originLabel(check.claimedOrigin),
      });
    case 'lien_escrow':
      return tk('webAdmin.listingDraft.check.lien_escrow', { kind });
    case 'energy_class_missing':
      return tk('webAdmin.listingDraft.check.energy_class_missing');
    // Krok PROP3 bod 4: zverejneny inzerat vychazi z udaje, ktery se zmenil.
    case 'facts_changed':
      return tk('webAdmin.listingDraft.check.facts_changed');
    /*
     * Krok NAP1 bod 1: inzerat ma adresu, ale souhlas majitele s presnou
     * polohou neni — na portaly jde ulice a obec. Veta JE ta sama, kterou
     * nese nahled exportu (`PublishStep`), aby kontrola a nahled netvrdily
     * o teze zakazce dve veci.
     */
    case 'precise_location_consent_missing':
      return tk('webAdmin.listingDraft.check.precise_location_consent_missing');
    /*
     * Krok PORT14: nadpis je delsi, nez unese nejprisnejsi napojeny portal.
     * Veta rika CISLA (kolik znaku ma a kolik portal bere), ne „je moc dlouhy" —
     * makler musi vedet, o kolik zkratit.
     */
    case 'title_too_long_for_portal':
      return tk('webAdmin.listingDraft.check.title_too_long_for_portal', {
        length: check.length,
        limit: check.limit,
      });
    case 'price_missing':
      return null;
  }
}

export function checkState(check: DraftCheck): 'done' | 'wait' {
  return check.severity === 'ok' ? 'done' : 'wait';
}

export function issueText(issue: PortalIssue): string {
  const portal = portalName(issue.portal);
  const values: Record<string, unknown> = { portal, field: fieldLabel(issue.field).toLowerCase(), ...(issue.context ?? {}) };
  return tk(`webAdmin.listingDraft.issue.${issue.code}`, values);
}

/** Co se s portalem po „Zveřejnit" stalo — poctive, vcetne „neodeslano". */
export function decisionText(decision: PublishDecision): string {
  const portal = portalName(decision.portal);
  if (decision.problem !== null) return tk('webAdmin.listingDraft.result.problem', { portal });
  return tk(`webAdmin.listingDraft.result.${decision.reason}`, { portal });
}

/** Hodnota pole -> text do editoru (a zpet `parseEditorValue`). */
export function editorText(field: DraftFieldView): string {
  const { value } = field;
  if (value === null || value === undefined) return '';
  if (field.valueType === 'money') {
    const amount = (value as { amount?: unknown }).amount;
    const currency = (value as { currency?: unknown }).currency;
    // Krok UXP5 bod 2: „6900000.0000" z API do pole NIKDY surove (viz `moneyInputAmount`).
    return typeof amount === 'string'
      ? moneyInputAmount({ amount, currency: typeof currency === 'string' ? currency : 'CZK' })
      : '';
  }
  if (field.valueType === 'date_or_now') {
    return availabilityText(value) ?? '';
  }
  if (typeof value === 'number') return number.format(value).replace(/\s/g, '');
  return typeof value === 'string' ? value : '';
}

/**
 * Text z editoru -> hodnota pro server. `undefined` = nesrozumitelne
 * (editor ukaze chybu), `null` = prazdne pole = zrusit prepis.
 */
export function parseEditorValue(field: Pick<DraftFieldView, 'valueType'>, raw: string): unknown {
  const text = raw.trim();
  if (text === '') return null;
  switch (field.valueType) {
    case 'number': {
      const n = Number(text.replace(/\s/g, '').replace(',', '.'));
      return Number.isFinite(n) && n > 0 ? n : undefined;
    }
    case 'integer': {
      const n = Number(text.replace(/\s/g, ''));
      return Number.isInteger(n) ? n : undefined;
    }
    /*
     * Krok UXP5 bod 2: tecka je DESETINNA TECKA, ne oddelovac tisicu. Puvodni
     * kod ji skrtl vzdy, takze „6900000.0000" z API se pri ulozeni promenilo
     * na „69000000000". Pravidlo je JEDNO (`parseMoneyInput`) — cte ho i pole
     * „Cenová představa" v zalozeni zakazky.
     */
    case 'money':
      return parseMoneyInput(text) ?? undefined;
    case 'date_or_now': {
      if (text.toLowerCase() === tk('webAdmin.listingDraft.value.now').toLowerCase()) return 'now';
      const m = /^(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{4})$/.exec(text);
      if (m !== null) {
        const [, d, mo, y] = m;
        return `${y ?? ''}-${(mo ?? '').padStart(2, '0')}-${(d ?? '').padStart(2, '0')}`;
      }
      /*
       * Krok P8F8 bod 1: cokoli jineho je DOSTUPNOST SLOVY („Po dohodě",
       * „po skončení nájmu"). Do P8F8 tu bylo `undefined` = editor hlasil
       * nesrozumitelnou hodnotu a text majitele se do inzeratu nedostal.
       * Strop 200 znaku je tyz jako v `normalizeFieldValue` (@reos/listing).
       */
      return text.slice(0, 200);
    }
    default:
      return text;
  }
}
